import _property from 'lodash/property';
import _get from 'lodash/get';
import _curry from 'lodash/curry';

// Utils
import selectorWithoutMemoize from '@tekion/tekion-base/utils/selectorWithoutMemoize';

// Helpers
import { EMPTY_OBJECT } from '@tekion/tekion-base/app.constants';
import { isOptionNew } from '../../../helpers/advancedSelect.general';

// Constants
import OPTION_MAPPER_BY_RESOURCE from '../configs';

/**
 * This HOF adds support for creatatable option. As this option is generated by select, we would want
 * our specific label getter to ignore this particular option and just return the default `label` field.
 * @param {Function} getOptionLabel: Label getter for a particular resource.
 * @param {Object} option: option object from which we need to extract label from.
 */
export var makeSelectLabelGetter = _curry(function (getOptionLabel, option) {
  // If the option is of create new type return label.
  if (isOptionNew(option)) {
    return option.label;
  }
  return getOptionLabel(option);
});

/**
 * This HOF adds support for creatatable option. As this option is generated by select, we would want
 * our specific label getter to ignore this particular option and just return the default `value` field.
 * @param {Function} getOptionLabel: Label getter for a particular resource.
 * @param {Object} option: option object from which we need to extract value from.
 */
export var makeSelectValueGetter = _curry(function (getOptionValue, option) {
  // If the option is of create new type return value.
  if (isOptionNew(option)) {
    return option.value;
  }
  return getOptionValue(option);
});

/**
 * This is the default label mapper. The key used is 'name' as it is expected to be generally used
 * for naming entities. If any entity has a special key please use OPTION_MAPPER_BY_RESOURCE.
 */
var defaultLabelGetter = _property('displayValue');

/**
 * This is the default value mapper. The key used is `id` as generally it is expected to be the value for any select.
 * If any special other key needs to be shown as the value please add the entry in OPTION_MAPPER_BY_RESOURCE.
 */
var defaultValueGetter = _property('id');

/**
 * This is the default label mapper. The key used is 'name' as it is expected to be generally used
 * for naming entities. If any entity has a special key please use OPTION_MAPPER_BY_RESOURCE.
 */
var defaultOptionDataGetter = _property('data');

/**
 * Get the label getter for the particular resource type. If any specific mapper exists then returns
 * the specific label getter else returns the default label getter.
 * @param {String} resourceType: Type of the resource which is being displayed in the select box.
 */
var makeOptionLabelGetter = function makeOptionLabelGetter(resourceType) {
  var resourceParams = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT;
  var getOptionLabel = resourceParams.getOptionLabel;
  var optionLabelGetterForResource = _get(OPTION_MAPPER_BY_RESOURCE, "".concat(resourceType, ".getOptionLabel"));
  return getOptionLabel || optionLabelGetterForResource || defaultLabelGetter;
};

/**
 * Get the value getter for the particular resource type. If any specific mapper exists then returns
 * the specific value getter else returns the default value getter.
 * @param {String} resourceType: Type of the resource which is being displayed in the select box.
 */
var makeOptionValueGetter = function makeOptionValueGetter(resourceType) {
  var resourceParams = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT;
  var getOptionValue = resourceParams.getOptionValue;
  var optionValueGetterForResource = _get(OPTION_MAPPER_BY_RESOURCE, "".concat(resourceType, ".getOptionValue"));
  return getOptionValue || optionValueGetterForResource || defaultValueGetter;
};

/**
 * Get the option-data getter for the particular resource type. If any specific mapper exists then returns
 * the specific value getter else returns the default value getter.
 * @param {String} resourceType: Type of the resource which is being displayed in the select box.
 */
var makeOptionDataGetter = function makeOptionDataGetter(resourceType) {
  var optionDataGetterForResource = _get(OPTION_MAPPER_BY_RESOURCE, "".concat(resourceType, ".getOptionData"));
  return optionDataGetterForResource || defaultOptionDataGetter;
};
var makeOptionMapperForResource = function makeOptionMapperForResource(getOptionLabel, getOptionValue, getOptionData) {
  return {
    getOptionLabel: makeSelectLabelGetter(getOptionLabel),
    getOptionValue: makeSelectValueGetter(getOptionValue),
    getOptionData: getOptionData
  };
};

/**
 * Generates the label and value getter for a particular resource types.
 */
var getOptionMapperForResource = selectorWithoutMemoize([makeOptionLabelGetter, makeOptionValueGetter, makeOptionDataGetter], makeOptionMapperForResource);
export default getOptionMapperForResource;